
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  FaUser,
  FaTag,
  FaVideo,
  FaImage,
  FaFileAlt,
  FaMusic,
  FaNewspaper,
  FaChartBar,
  FaBullhorn,
  FaGlobe,
  FaPenAlt,
  FaEye,
  FaHeart,
  FaShareAlt,
  FaSearch,
  FaStar,
  FaPlayCircle,
  FaHeadphones,
  FaDownload,
  FaExternalLinkAlt,
  FaBookOpen,
  FaExclamationCircle,
  FaTimes,
  FaPlay,
  FaArrowLeft,
  FaFilter,
  FaChevronDown,
  FaRegClock,
  FaThLarge,
  FaList,
  FaSquare,
  FaAngleDoubleLeft,
  FaMosque,
  FaPray,
  FaQuran,
  FaClock,
  FaArrowUp,
  FaBars,
  FaChevronLeft,
  FaChevronRight,
  FaCircle
} from 'react-icons/fa';

const Home = () => {
  const [content, setContent] = useState([]);
  const [contentWithTokens, setContentWithTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContent, setFilteredContent] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [likedItems, setLikedItems] = useState({});
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  
  // Hero Slider States
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [sliderLoading, setSliderLoading] = useState(true);

  // Load liked items from localStorage on component mount (only once)
  useEffect(() => {
    try {
      const storedLikes = localStorage.getItem('likedItems');
      if (storedLikes) {
        const parsedLikes = JSON.parse(storedLikes);
        setLikedItems(parsedLikes);
      }
    } catch (error) {
      console.error('Error parsing liked items from localStorage:', error);
      localStorage.removeItem('likedItems');
    }
  }, []);

  // Save liked items to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(likedItems).length > 0) {
      localStorage.setItem('likedItems', JSON.stringify(likedItems));
    } else {
      localStorage.removeItem('likedItems');
    }
  }, [likedItems]);

  // Define the categories
  const categories = [
    { id: 'all', name: 'الكل', icon: <FaGlobe />, color: '#6366f1' },
    { id: 'video', name: 'فيديوهات', icon: <FaVideo />, color: '#3b82f6' },
    { id: 'image', name: 'صور', icon: <FaImage />, color: '#10b981' }
  ];

  // Get category details
  const getCategoryDetails = (categoryId) => {
    return categories.find(cat => cat.id === categoryId) || categories[0];
  };

  // Handle scroll to show/hide scroll top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Filter content based on search and category
  useEffect(() => {
    let filtered = contentWithTokens;
    
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => 
        item.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.content_type === selectedCategory);
    }

    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'most_liked') {
      filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    }
    
    setFilteredContent(filtered);
  }, [searchTerm, selectedCategory, contentWithTokens, sortBy]);

  // Generate Cloudinary thumbnail URL
  const generateCloudinaryThumbnail = (videoUrl, options = {}) => {
    if (!videoUrl) return null;
    
    const defaultOptions = {
      width: 400,
      height: 225,
      crop: 'fill',
      quality: 'auto:good',
      format: 'jpg'
    };
    
    const thumbOptions = { ...defaultOptions, ...options };
    const transformations = [
      `c_${thumbOptions.crop}`,
      `w_${thumbOptions.width}`,
      `h_${thumbOptions.height}`,
      `q_${thumbOptions.quality}`,
      `f_${thumbOptions.format}`
    ].join(',');
    
    return videoUrl.replace('/upload/', `/upload/${transformations}/`);
  };

  const getThumbnailUrl = (item) => {
    if (!item.featured_media) return null;
    
    if (item.content_type === 'video' && item.featured_media.includes('cloudinary.com')) {
      return generateCloudinaryThumbnail(item.featured_media);
    }
    
    if (item.content_type === 'image') {
      return item.featured_media;
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'الآن';
    } else if (diffInHours < 24) {
      return `منذ ${diffInHours} ساعة`;
    } else if (diffInHours < 48) {
      return 'أمس';
    } else {
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getContentTypeIcon = (contentType) => {
    const icons = {
      'video': <FaVideo />,
      'image': <FaImage />,
      'default': <FaBookOpen />
    };
    return icons[contentType] || icons.default;
  };

  const getContentTypeText = (contentType) => {
    const types = {
      'video': 'فيديو',
      'image': 'صورة',
      'default': 'محتوى'
    };
    return types[contentType] || types.default;
  };

  const getContentTypeColor = (contentType) => {
    const colors = {
      'video': '#3b82f6',
      'image': '#10b981',
      'default': '#6b7280'
    };
    return colors[contentType] || colors.default;
  };

  const fetchTokensForContent = async (contentItems) => {
    try {
      const contentIds = contentItems.map(item => item.id);
      
      const { data: tokensData, error } = await supabase
        .from('content_tokens')
        .select('token, content_id')
        .in('content_id', contentIds);

      if (error) throw error;

      const tokenMap = {};
      if (tokensData) {
        tokensData.forEach(token => {
          tokenMap[token.content_id] = token.token;
        });
      }

      return contentItems.map(item => ({
        ...item,
        content_token: tokenMap[item.id] || null
      }));
    } catch (error) {
      console.error('Error fetching tokens:', error);
      return contentItems.map(item => ({
        ...item,
        content_token: null
      }));
    }
  };

  // Fetch content - only depends on empty array to run once on mount
  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data: contentData, error } = await supabase
        .from('content')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const contentItems = contentData || [];
      setContent(contentItems);
      
      const contentWithTokens = await fetchTokensForContent(contentItems);
      setContentWithTokens(contentWithTokens);
      setFilteredContent(contentWithTokens);
      
      // Prepare hero slides (take first 5 items or items with featured flag)
      const featuredItems = contentItems
        .filter(item => item.featured === true || item.is_featured === true)
        .slice(0, 5);
      
      // If no featured items, take the most recent 5
      const slidesToUse = featuredItems.length > 0 ? featuredItems : contentItems.slice(0, 5);
      
      // Fetch tokens for hero slides
      const slidesWithTokens = await fetchTokensForContent(slidesToUse);
      setHeroSlides(slidesWithTokens);
      setSliderLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setSliderLoading(false);
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - only runs once

  // Initial fetch on mount
  useEffect(() => {
    fetchContent();

    const subscription = supabase
      .channel('content_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'content' },
        () => {
          fetchContent();
        }
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'content' },
        () => {
          fetchContent();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [fetchContent]);

  // Hero Slider Auto-play
  useEffect(() => {
    let interval;
    if (isAutoPlaying && heroSlides.length > 1) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides.length]);

  const handleQuickView = (item) => {
    setSelectedContent(item);
    setShowQuickView(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortBy('newest');
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} د`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} س`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)} ي`;
    }
  };

  const handleLike = async (itemId, e) => {
    e.stopPropagation();
    
    try {
      // Find the current item
      const currentItem = contentWithTokens.find(c => c.id === itemId);
      if (!currentItem) return;

      const isLiked = likedItems[itemId];

      if (isLiked) {
        // Unlike current item
        const newLikedItems = { ...likedItems };
        delete newLikedItems[itemId];
        
        // Update local state
        setLikedItems(newLikedItems);
        
        // Update content in state (decrease like count)
        setContentWithTokens(prev => 
          prev.map(item => {
            if (item.id === itemId) {
              return { ...item, likes: Math.max(0, (item.likes || 0) - 1) };
            }
            return item;
          })
        );

        setFilteredContent(prev => 
          prev.map(item => {
            if (item.id === itemId) {
              return { ...item, likes: Math.max(0, (item.likes || 0) - 1) };
            }
            return item;
          })
        );

        // Update hero slides
        setHeroSlides(prev => 
          prev.map(item => {
            if (item.id === itemId) {
              return { ...item, likes: Math.max(0, (item.likes || 0) - 1) };
            }
            return item;
          })
        );

        if (selectedContent && selectedContent.id === itemId) {
          setSelectedContent(prev => ({
            ...prev,
            likes: Math.max(0, (prev.likes || 0) - 1)
          }));
        }
        
        // Update in database
        await supabase
          .from('content')
          .update({ likes: Math.max(0, (currentItem.likes || 0) - 1) })
          .eq('id', itemId);
        
      } else {
        // Like new item - keep all existing likes
        const newLikedItems = { ...likedItems, [itemId]: true };
        
        // Update local state
        setLikedItems(newLikedItems);
        
        // Update content in state (increase like count)
        setContentWithTokens(prev => 
          prev.map(item => {
            if (item.id === itemId) {
              return { ...item, likes: (item.likes || 0) + 1 };
            }
            return item;
          })
        );

        setFilteredContent(prev => 
          prev.map(item => {
            if (item.id === itemId) {
              return { ...item, likes: (item.likes || 0) + 1 };
            }
            return item;
          })
        );

        // Update hero slides
        setHeroSlides(prev => 
          prev.map(item => {
            if (item.id === itemId) {
              return { ...item, likes: (item.likes || 0) + 1 };
            }
            return item;
          })
        );

        if (selectedContent && selectedContent.id === itemId) {
          setSelectedContent(prev => ({
            ...prev,
            likes: (prev.likes || 0) + 1
          }));
        }
        
        // Update in database
        await supabase
          .from('content')
          .update({ likes: (currentItem.likes || 0) + 1 })
          .eq('id', itemId);
      }
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  const handleShare = async (item, e) => {
    e.stopPropagation();
    
    const url = `${window.location.origin}/content/${item.content_token || item.id}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setShareUrl(url);
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Add like button to each content card based on view mode
  const renderLikeButton = (item) => {
    const isLiked = likedItems[item.id];
    
    return (
      <button 
        className={`like-button ${isLiked ? 'liked' : ''}`}
        onClick={(e) => handleLike(item.id, e)}
      >
        <FaHeart />
        <span>{item.likes || 0}</span>
      </button>
    );
  };

  // Add share button to each content card based on view mode
  const renderShareButton = (item) => {
    return (
      <button 
        className="share-button"
        onClick={(e) => handleShare(item, e)}
      >
        <FaShareAlt />
      </button>
    );
  };

  // Hero Slider navigation
  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false); // Pause auto-play on manual navigation
    setTimeout(() => setIsAutoPlaying(true), 5000); // Resume after 5 seconds
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  return (
    <div className="home-container">
      <style>{`
        /* Scoped Styles - Only affect elements inside .home-container */
        .home-container {
          min-height: 100vh;
          background: #0a0a0a;
          font-family: 'ITF Qomra Arabic', 'Segoe UI', sans-serif;
          direction: rtl;
          color: #ffffff;
          padding-top: 2rem;
          position: relative;
        }

        /* Share Tooltip */
        .home-container .share-tooltip {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: #10b981;
          color: white;
          padding: 12px 24px;
          border-radius: 40px;
          font-size: 0.95rem;
          z-index: 3000;
          animation: slideDown 0.3s ease;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        @keyframes slideDown {
          from {
            top: -50px;
            opacity: 0;
          }
          to {
            top: 20px;
            opacity: 1;
          }
        }

        /* Hero Slider Styles */
        .home-container .hero-slider-container {
          position: relative;
          width: 100%;
          height: 600px;
          overflow: hidden;
          border-radius: 30px;
          margin-bottom: 3rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .home-container .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          transition: opacity 0.8s ease-in-out;
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          padding: 4rem;
        }

        .home-container .hero-slide.active {
          opacity: 1;
        }

        .home-container .hero-slide::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%);
          z-index: 1;
        }

        .home-container .hero-content {
          position: relative;
          z-index: 2;
          max-width: 700px;
          animation: slideInRight 0.8s ease-out;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .home-container .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 40px;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
        }

        .home-container .hero-badge svg {
          font-size: 1rem;
        }

        .home-container .hero-title {
          font-size: 3.5rem;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1rem;
          color: #ffffff;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .home-container .hero-description {
          font-size: 1.2rem;
          line-height: 1.6;
          margin-bottom: 2rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
        }

        .home-container .hero-meta {
          display: flex;
          align-items: center;
          gap: 2rem;
          margin-bottom: 2rem;
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        .home-container .hero-meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .home-container .hero-actions {
          display: flex;
          gap: 1rem;
        }

        .home-container .hero-button {
          padding: 1rem 2rem;
          border-radius: 50px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
        }

        .home-container .hero-button.primary {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
        }

        .home-container .hero-button.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.4);
        }

        .home-container .hero-button.secondary {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .home-container .hero-button.secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .home-container .hero-nav {
          position: absolute;
          bottom: 2rem;
          right: 4rem;
          z-index: 10;
          display: flex;
          gap: 1rem;
        }

        .home-container .hero-nav-button {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1.2rem;
        }

        .home-container .hero-nav-button:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
        }

        .home-container .hero-dots {
          position: absolute;
          bottom: 2rem;
          left: 4rem;
          z-index: 10;
          display: flex;
          gap: 0.75rem;
        }

        .home-container .hero-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          border: 2px solid transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .home-container .hero-dot.active {
          background: #6366f1;
          transform: scale(1.2);
          box-shadow: 0 0 20px #6366f1;
        }

        .home-container .hero-dot:hover {
          background: rgba(255, 255, 255, 0.6);
        }

        .home-container .hero-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
        }

        .home-container .hero-spinner {
          width: 50px;
          height: 50px;
          border: 3px solid rgba(99, 102, 241, 0.3);
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        /* Main Container */
        .home-container .main-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 2rem 2rem 2rem;
        }

        /* Search Bar */
        .home-container .search-wrapper {
          position: relative;
          max-width: 600px;
          margin: 0 auto 3rem auto;
        }

        .home-container .search-wrapper input {
          width: 100%;
          padding: 1rem 1.5rem 1rem 4rem;
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 60px;
          color: #ffffff;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .home-container .search-wrapper input:focus {
          outline: none;
          border-color: #6366f1;
          background: #1f1f1f;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .home-container .search-wrapper input::placeholder {
          color: #525252;
        }

        .home-container .search-wrapper button {
          position: absolute;
          left: 1.5rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #a3a3a3;
          cursor: pointer;
          font-size: 1.2rem;
          transition: color 0.2s ease;
        }

        .home-container .search-wrapper button:hover {
          color: #6366f1;
        }

        /* Categories Bar */
        .home-container .categories-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding: 0.5rem;
          background: #111111;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 60px;
          overflow-x: auto;
          flex-wrap: nowrap;
        }

        .home-container .category-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          background: transparent;
          border: none;
          border-radius: 40px;
          color: #a3a3a3;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .home-container .category-btn svg {
          font-size: 1.1rem;
        }

        .home-container .category-btn:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.05);
        }

        .home-container .category-btn.active {
          background: rgba(99, 102, 241, 0.1);
          color: #818cf8;
        }

        /* Controls Bar */
        .home-container .controls-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2rem;
          padding: 1rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .home-container .view-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .home-container .view-toggle {
          display: flex;
          gap: 0.5rem;
          background: #1a1a1a;
          padding: 0.25rem;
          border-radius: 12px;
        }

        .home-container .view-toggle-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          color: #737373;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1.1rem;
        }

        .home-container .view-toggle-btn:hover {
          color: #ffffff;
        }

        .home-container .view-toggle-btn.active {
          background: #2a2a2a;
          color: #818cf8;
        }

        .home-container .sort-controls {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .home-container .sort-label {
          color: #a3a3a3;
          font-size: 0.9rem;
        }

        .home-container .sort-select {
          padding: 0.5rem 2rem 0.5rem 1rem;
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          color: #ffffff;
          font-size: 0.9rem;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: left 0.75rem center;
        }

        .home-container .sort-select option {
          background: #1a1a1a;
          color: #ffffff;
        }

        /* Results Count */
        .home-container .results-count {
          margin-bottom: 1.5rem;
          color: #a3a3a3;
          font-size: 0.95rem;
          background: rgba(255, 255, 255, 0.05);
          padding: 0.5rem 1.25rem;
          border-radius: 40px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          display: inline-block;
        }

        /* Grid View */
        .home-container .content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 2rem;
        }

        .home-container .content-card {
          background: #111111;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          position: relative;
        }

        .home-container .content-card:hover {
          transform: translateY(-4px);
          border-color: #6366f1;
          box-shadow: 0 20px 30px -10px rgba(0, 0, 0, 0.5);
        }

        .home-container .card-media {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .home-container .card-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .home-container .content-card:hover .card-media img {
          transform: scale(1.05);
        }

        .home-container .card-type-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          color: #ffffff;
          padding: 0.5rem 1rem;
          border-radius: 30px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .home-container .card-time {
          position: absolute;
          bottom: 1rem;
          left: 1rem;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          color: #ffffff;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .home-container .card-badges {
          position: absolute;
          top: 1rem;
          left: 1rem;
          display: flex;
          gap: 0.5rem;
        }

        .home-container .card-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 600;
          color: #ffffff;
        }

        .home-container .card-badge.breaking {
          background: #dc2626;
        }

        .home-container .card-badge.featured {
          background: #f59e0b;
        }

        .home-container .card-content {
          padding: 1.5rem;
        }

        .home-container .card-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.75rem;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .home-container .card-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          color: #a3a3a3;
          font-size: 0.85rem;
          margin-bottom: 1rem;
        }

        .home-container .card-meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .home-container .card-stats {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-top: 0.5rem;
          margin-bottom: 1rem;
        }

        .home-container .card-stat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #a3a3a3;
          font-size: 0.85rem;
        }

        .home-container .card-stat svg {
          font-size: 1rem;
        }

        .home-container .like-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: transparent;
          border: none;
          color: #a3a3a3;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.85rem;
        }

        .home-container .like-button:hover {
          color: #ef4444;
        }

        .home-container .like-button.liked {
          color: #ef4444;
        }

        .home-container .like-button.liked svg {
          color: #ef4444;
        }

        .home-container .share-button {
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: #a3a3a3;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1rem;
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }

        .home-container .share-button:hover {
          color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
        }

        .home-container .card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .home-container .card-button {
          padding: 0.6rem 1.5rem;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 30px;
          color: #ffffff;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .home-container .card-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(99, 102, 241, 0.3);
        }

        /* List View */
        .home-container .content-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .home-container .content-list-item {
          background: #111111;
          border-radius: 16px;
          padding: 1.25rem;
          display: flex;
          gap: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .home-container .content-list-item:hover {
          border-color: #6366f1;
          background: #1a1a1a;
        }

        .home-container .list-item-image {
          width: 120px;
          height: 80px;
          border-radius: 12px;
          overflow: hidden;
          flex-shrink: 0;
        }

        .home-container .list-item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .home-container .list-item-content {
          flex: 1;
          min-width: 0;
        }

        .home-container .list-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .home-container .list-item-title {
          font-size: 1rem;
          font-weight: 600;
          color: #ffffff;
        }

        .home-container .list-item-type {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.75rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 20px;
          color: #818cf8;
          font-size: 0.7rem;
        }

        .home-container .list-item-meta {
          display: flex;
          gap: 1.5rem;
          color: #a3a3a3;
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
        }

        .home-container .list-item-stats {
          display: flex;
          gap: 1.5rem;
          color: #a3a3a3;
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
        }

        .home-container .list-item-stat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .home-container .list-item-stat.liked svg {
          color: #ef4444;
        }

        .home-container .list-item-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .home-container .list-item-link {
          padding: 0.5rem 1.25rem;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 30px;
          color: #ffffff;
          text-decoration: none;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }

        .home-container .list-item-link:hover {
          transform: translateX(-2px);
          box-shadow: 0 5px 10px rgba(99, 102, 241, 0.3);
        }

        /* Compact View */
        .home-container .content-compact {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .home-container .content-compact-item {
          background: #111111;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .home-container .content-compact-item:hover {
          border-color: #6366f1;
          transform: translateY(-2px);
        }

        .home-container .compact-image {
          height: 140px;
          overflow: hidden;
        }

        .home-container .compact-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .home-container .compact-content {
          padding: 1rem;
        }

        .home-container .compact-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.5rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .home-container .compact-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #a3a3a3;
          font-size: 0.75rem;
        }

        .home-container .compact-type {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.2rem 0.5rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 12px;
          color: #818cf8;
        }

        .home-container .compact-stats {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.5rem;
          color: #a3a3a3;
          font-size: 0.75rem;
        }

        .home-container .compact-stat {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .home-container .compact-stat.liked svg {
          color: #ef4444;
        }

        .home-container .compact-footer {
          margin-top: 0.75rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .home-container .compact-button {
          padding: 0.4rem 1rem;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 20px;
          color: #ffffff;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .home-container .compact-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 10px rgba(99, 102, 241, 0.3);
        }

        /* Quick View Modal */
        .home-container .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 2rem;
        }

        .home-container .modal-content {
          background: #111111;
          border-radius: 24px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .home-container .modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          background: #111111;
          z-index: 10;
        }

        .home-container .modal-header h2 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #ffffff;
        }

        .home-container .modal-close {
          background: none;
          border: none;
          color: #a3a3a3;
          font-size: 1.2rem;
          cursor: pointer;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .home-container .modal-close:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #ffffff;
        }

        .home-container .modal-body {
          padding: 1.5rem;
        }

        .home-container .modal-image {
          width: 100%;
          height: 300px;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 1.5rem;
        }

        .home-container .modal-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .home-container .modal-description {
          color: #a3a3a3;
          line-height: 1.8;
          margin-bottom: 1.5rem;
        }

        .home-container .modal-stats {
          display: flex;
          gap: 2rem;
          margin-bottom: 1.5rem;
          color: #a3a3a3;
        }

        .home-container .modal-stat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .home-container .modal-actions {
          display: flex;
          gap: 1rem;
        }

        .home-container .modal-button {
          flex: 1;
          padding: 1rem;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 12px;
          color: #ffffff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .home-container .modal-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(99, 102, 241, 0.3);
        }

        .home-container .modal-button.like {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .home-container .modal-button.like:hover {
          background: #2a2a2a;
        }

        .home-container .modal-button.like.liked {
          color: #ef4444;
          border-color: #ef4444;
        }

        .home-container .modal-button.share {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .home-container .modal-button.share:hover {
          background: #2a2a2a;
          color: #3b82f6;
          border-color: #3b82f6;
        }

        /* Loading States */
        .home-container .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 5rem;
          min-height: 400px;
        }

        .home-container .loading-spinner {
          width: 50px;
          height: 50px;
          border: 3px solid rgba(99, 102, 241, 0.1);
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1.5rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Empty State */
        .home-container .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: #111111;
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .home-container .empty-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          color: #404040;
        }

        .home-container .empty-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .home-container .empty-text {
          color: #a3a3a3;
        }

        /* Scroll to Top */
        .home-container .scroll-top {
          position: fixed;
          bottom: 2rem;
          left: 2rem;
          width: 50px;
          height: 50px;
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #818cf8;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 100;
          opacity: 0;
          visibility: hidden;
        }

        .home-container .scroll-top.visible {
          opacity: 1;
          visibility: visible;
        }

        .home-container .scroll-top:hover {
          background: #2a2a2a;
          transform: translateY(-4px);
        }

        /* Category Badge */
        .home-container .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.25rem 1rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .home-container .hero-title {
            font-size: 2.5rem;
          }
          
          .home-container .hero-description {
            font-size: 1rem;
          }
        }

        @media (max-width: 992px) {
          .home-container .hero-slider-container {
            height: 500px;
          }
          
          .home-container .hero-title {
            font-size: 2rem;
          }
          
          .home-container .search-wrapper input {
            font-size: 0.95rem;
          }
          
          .home-container .categories-bar {
            justify-content: flex-start;
          }
        }

        @media (max-width: 768px) {
          .home-container {
            padding-top: 1rem;
          }
          
          .home-container .hero-slider-container {
            height: 400px;
            border-radius: 20px;
          }
          
          .home-container .hero-slide {
            padding: 2rem;
          }
          
          .home-container .hero-title {
            font-size: 1.5rem;
          }
          
          .home-container .hero-description {
            font-size: 0.9rem;
            margin-bottom: 1rem;
          }
          
          .home-container .hero-badge {
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
          }
          
          .home-container .hero-meta {
            gap: 1rem;
            font-size: 0.9rem;
          }
          
          .home-container .hero-actions {
            flex-direction: column;
          }
          
          .home-container .hero-button {
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
          }
          
          .home-container .hero-nav {
            bottom: 1rem;
            right: 1rem;
          }
          
          .home-container .hero-dots {
            bottom: 1rem;
            left: 1rem;
          }
          
          .home-container .controls-bar {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }
          
          .home-container .view-controls {
            width: 100%;
            justify-content: space-between;
          }
          
          .home-container .content-grid {
            grid-template-columns: 1fr;
          }
          
          .home-container .content-list-item {
            flex-direction: column;
          }
          
          .home-container .list-item-image {
            width: 100%;
            height: 150px;
          }
          
          .home-container .categories-bar {
            padding: 0.5rem;
            gap: 0.5rem;
          }
          
          .home-container .category-btn {
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .home-container .hero-slider-container {
            height: 350px;
          }
          
          .home-container .hero-slide {
            padding: 1.5rem;
          }
          
          .home-container .hero-title {
            font-size: 1.2rem;
          }
          
          .home-container .hero-badge {
            font-size: 0.7rem;
          }
          
          .home-container .hero-meta {
            flex-wrap: wrap;
          }
        }
      `}</style>

      {/* Share Tooltip */}
      {showShareTooltip && (
        <div className="share-tooltip">
          تم نسخ الرابط! ✅
        </div>
      )}

      {/* Main Content */}
      <main className="main-container">
        {/* Hero Slider */}
        <div className="hero-slider-container">
          {sliderLoading ? (
            <div className="hero-loading">
              <div className="hero-spinner"></div>
            </div>
          ) : (
            <>
              {heroSlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                  style={{
                    backgroundImage: `url(${getThumbnailUrl(slide) || 'https://via.placeholder.com/1920x600?text=أمجاد+الأحرار'})`
                  }}
                >
                  <div className="hero-content">
                    <div className="hero-badge">
                      {getContentTypeIcon(slide.content_type)}
                      <span>{getContentTypeText(slide.content_type)}</span>
                    </div>
                    
                    <h1 className="hero-title">{slide.title_ar || slide.title}</h1>
                    
                    {slide.description_ar && (
                      <p className="hero-description">{slide.description_ar}</p>
                    )}
                    
                    <div className="hero-meta">
                      <span className="hero-meta-item">
                        <FaUser />
                        {slide.author_ar || slide.author || 'أمجاد الأحرار'}
                      </span>
                      <span className="hero-meta-item">
                        <FaClock />
                        {formatDate(slide.created_at)}
                      </span>
                      <span className="hero-meta-item">
                        <FaHeart className={likedItems[slide.id] ? 'liked' : ''} />
                        {slide.likes || 0} إعجاب
                      </span>
                    </div>
                    
                    <div className="hero-actions">
                      <Link
                        to={`/content/${slide.content_token || slide.id}`}
                        className="hero-button primary"
                      >
                        <FaPlay />
                        مشاهدة الآن
                      </Link>
                      <button
                        className="hero-button secondary"
                        onClick={() => handleQuickView(slide)}
                      >
                        <FaEye />
                        معاينة سريعة
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Navigation Buttons */}
              {heroSlides.length > 1 && (
                <>
                  <div className="hero-nav">
                    <button className="hero-nav-button" onClick={prevSlide}>
                      <FaChevronRight />
                    </button>
                    <button className="hero-nav-button" onClick={nextSlide}>
                      <FaChevronLeft />
                    </button>
                  </div>
                  
                  {/* Dots */}
                  <div className="hero-dots">
                    {heroSlides.map((_, index) => (
                      <button
                        key={index}
                        className={`hero-dot ${index === currentSlide ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Search Bar */}
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="ابحث في المحتوى..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button>
            <FaSearch />
          </button>
        </div>

        {/* Categories Bar */}
        <div className="categories-bar">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.icon}
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* Controls Bar */}
        <div className="controls-bar">
          <div className="view-controls">
            <div className="view-toggle">
              <button 
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <FaThLarge />
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <FaList />
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'compact' ? 'active' : ''}`}
                onClick={() => setViewMode('compact')}
              >
                <FaSquare />
              </button>
            </div>
          </div>

          <div className="sort-controls">
            <span className="sort-label">ترتيب:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">الأحدث</option>
              <option value="oldest">الأقدم</option>
              <option value="most_liked">الأكثر إعجاباً</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="results-count">
          {filteredContent.length} نتيجة
        </div>

        {/* Content Grid/List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p style={{ color: '#a3a3a3' }}>جاري تحميل المحتوى...</p>
          </div>
        ) : filteredContent.length > 0 ? (
          <>
            {viewMode === 'grid' && (
              <div className="content-grid">
                {filteredContent.map((item, index) => {
                  const category = getCategoryDetails(item.content_type);
                  return (
                    <div key={index} className="content-card" onClick={() => handleQuickView(item)}>
                      <div className="card-media">
                        {getThumbnailUrl(item) ? (
                          <img src={getThumbnailUrl(item)} alt={item.title_ar || item.title} />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: getContentTypeColor(item.content_type),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            color: '#ffffff'
                          }}>
                            {getContentTypeIcon(item.content_type)}
                          </div>
                        )}
                        <div className="card-type-badge">
                          {getContentTypeIcon(item.content_type)}
                          <span>{getContentTypeText(item.content_type)}</span>
                        </div>
                        <div className="card-time">
                          <FaClock />
                          {getTimeAgo(item.created_at)}
                        </div>
                      </div>
                      
                      <div className="card-content">
                        <h3 className="card-title">{item.title_ar || item.title}</h3>
                        
                        <div className="card-meta">
                          <span className="card-meta-item">
                            <FaUser />
                            {item.author_ar || item.author || 'أمجاد الأحرار'}
                          </span>
                        </div>

                        <div className="card-stats">
                          {renderLikeButton(item)}
                        </div>
                        
                        <div className="card-footer">
                          <Link 
                            to={`/content/${item.content_token || item.id}`}
                            className="card-button"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FaArrowLeft />
                            عرض التفاصيل
                          </Link>
                          {renderShareButton(item)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {viewMode === 'list' && (
              <div className="content-list">
                {filteredContent.map((item, index) => {
                  const category = getCategoryDetails(item.content_type);
                  return (
                    <div key={index} className="content-list-item" onClick={() => handleQuickView(item)}>
                      <div className="list-item-image">
                        {getThumbnailUrl(item) ? (
                          <img src={getThumbnailUrl(item)} alt={item.title_ar || item.title} />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: getContentTypeColor(item.content_type),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff'
                          }}>
                            {getContentTypeIcon(item.content_type)}
                          </div>
                        )}
                      </div>
                      
                      <div className="list-item-content">
                        <div className="list-item-header">
                          <h3 className="list-item-title">{item.title_ar || item.title}</h3>
                          <span className="list-item-type">
                            {getContentTypeIcon(item.content_type)}
                            {getContentTypeText(item.content_type)}
                          </span>
                        </div>
                        
                        <div className="list-item-meta">
                          <span>{item.author_ar || item.author || 'أمجاد الأحرار'}</span>
                          <span>•</span>
                          <span>{formatDate(item.created_at)}</span>
                        </div>

                        <div className="list-item-stats">
                          <span className={`list-item-stat ${likedItems[item.id] ? 'liked' : ''}`}>
                            <FaHeart />
                            {item.likes || 0}
                          </span>
                        </div>
                        
                        <div className="list-item-footer">
                          <button 
                            className={`like-button ${likedItems[item.id] ? 'liked' : ''}`}
                            onClick={(e) => handleLike(item.id, e)}
                          >
                            <FaHeart />
                            {likedItems[item.id] ? 'تم الإعجاب' : 'إعجاب'}
                          </button>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {renderShareButton(item)}
                            <Link 
                              to={`/content/${item.content_token || item.id}`} 
                              className="list-item-link" 
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaArrowLeft />
                              عرض التفاصيل
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {viewMode === 'compact' && (
              <div className="content-compact">
                {filteredContent.map((item, index) => {
                  const category = getCategoryDetails(item.content_type);
                  return (
                    <div key={index} className="content-compact-item" onClick={() => handleQuickView(item)}>
                      <div className="compact-image">
                        {getThumbnailUrl(item) ? (
                          <img src={getThumbnailUrl(item)} alt={item.title_ar || item.title} />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: getContentTypeColor(item.content_type),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem',
                            color: '#ffffff'
                          }}>
                            {getContentTypeIcon(item.content_type)}
                          </div>
                        )}
                      </div>
                      <div className="compact-content">
                        <h4 className="compact-title">{item.title_ar || item.title}</h4>
                        <div className="compact-meta">
                          <span>{formatDate(item.created_at)}</span>
                          <span className="compact-type">
                            {getContentTypeIcon(item.content_type)}
                            {getContentTypeText(item.content_type)}
                          </span>
                        </div>
                        <div className="compact-stats">
                          <span className={`compact-stat ${likedItems[item.id] ? 'liked' : ''}`}>
                            <FaHeart />
                            {item.likes || 0}
                          </span>
                        </div>
                        <div className="compact-footer">
                          <button 
                            className={`like-button ${likedItems[item.id] ? 'liked' : ''}`}
                            onClick={(e) => handleLike(item.id, e)}
                          >
                            <FaHeart />
                          </button>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {renderShareButton(item)}
                            <Link 
                              to={`/content/${item.content_token || item.id}`}
                              className="compact-button"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FaArrowLeft />
                              عرض
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaFileAlt />
            </div>
            <h3 className="empty-title">لا يوجد محتوى</h3>
            <p className="empty-text">جرب تغيير معايير البحث</p>
          </div>
        )}
      </main>

      {/* Quick View Modal */}
      {showQuickView && selectedContent && (
        <div className="modal-overlay" onClick={() => setShowQuickView(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedContent.title_ar || selectedContent.title}</h2>
              <button className="modal-close" onClick={() => setShowQuickView(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-image">
                {getThumbnailUrl(selectedContent) ? (
                  <img src={getThumbnailUrl(selectedContent)} alt={selectedContent.title_ar || selectedContent.title} />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: getContentTypeColor(selectedContent.content_type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                    color: '#ffffff'
                  }}>
                    {getContentTypeIcon(selectedContent.content_type)}
                  </div>
                )}
              </div>

              <div className="modal-stats">
                <span className="modal-stat">
                  <FaHeart />
                  {selectedContent.likes || 0} إعجاب
                </span>
                <span className="modal-stat">
                  <FaClock />
                  {formatDate(selectedContent.created_at)}
                </span>
              </div>
         
              {selectedContent.description && (
                <p className="modal-description">
                  {selectedContent.description_ar || selectedContent.description}
                </p>
              )}

              <div className="modal-actions">
                <button 
                  className={`modal-button like ${likedItems[selectedContent.id] ? 'liked' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLike(selectedContent.id, e);
                  }}
                >
                  <FaHeart />
                  {likedItems[selectedContent.id] ? 'تم الإعجاب' : 'إعجاب'}
                  <span>({selectedContent.likes || 0})</span>
                </button>
                <button 
                  className="modal-button share"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(selectedContent, e);
                  }}
                >
                  <FaShareAlt />
                  مشاركة
                </button>
                <Link 
                  to={`/content/${selectedContent.content_token || selectedContent.id}`}
                  className="modal-button"
                  onClick={() => setShowQuickView(false)}
                >
                  <FaArrowLeft />
                  عرض التفاصيل
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top */}
      <button 
        className={`scroll-top ${showScrollTop ? 'visible' : ''}`} 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <FaArrowUp />
      </button>
    </div>
  );
};

export default Home;